import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { shared } from './shared';
import { WORLD } from '../config/zones';

// 'Outrage Storm' — glowing crimson data-packets rain down when a storm hits.
export default function Weather() {
  const points = useRef();
  const COUNT = 1600;

  const { positions, speeds } = useMemo(() => {
    const positions = new Float32Array(COUNT * 3);
    const speeds = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      positions[i * 3] = WORLD.xMin + Math.random() * (WORLD.xMax - WORLD.xMin);
      positions[i * 3 + 1] = Math.random() * 120;
      positions[i * 3 + 2] = WORLD.zMin + Math.random() * (WORLD.zMax - WORLD.zMin);
      speeds[i] = 30 + Math.random() * 60;
    }
    return { positions, speeds };
  }, []);

  useFrame((state, delta) => {
    const dt = Math.min(delta, 0.05);
    const storm = shared.env.storm;
    if (!points.current) return;
    const mat = points.current.material;
    mat.opacity = storm * 0.9;
    points.current.visible = storm > 0.02;
    // Rain density scales with the storm intensity slider (capped low in reduced-FX mode)
    const cap = shared.lowFX ? 0.14 : 1;
    points.current.geometry.setDrawRange(0, Math.floor(COUNT * Math.min(1, storm) * cap));
    if (storm <= 0.02) return;

    const wind = 40 * storm;
    const arr = points.current.geometry.attributes.position.array;
    for (let i = 0; i < COUNT; i++) {
      arr[i * 3 + 1] -= speeds[i] * dt;
      arr[i * 3] += wind * dt * (0.5 + 0.5 * Math.sin(state.clock.elapsedTime + i));
      if (arr[i * 3 + 1] < 0) {
        arr[i * 3 + 1] = 100 + Math.random() * 40;
        arr[i * 3] = WORLD.xMin + Math.random() * (WORLD.xMax - WORLD.xMin);
        arr[i * 3 + 2] = WORLD.zMin + Math.random() * (WORLD.zMax - WORLD.zMin);
      }
    }
    points.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={points} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={COUNT} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial
        color="#8b5cf6"
        size={2.6}
        sizeAttenuation
        transparent
        opacity={0}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
